import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ScanStatusBadge } from './ScanStatusBadge';
import { CheckIcon, EditIcon, SpinnerIcon, TrashIcon, XIcon } from './StatusIcons';
import { UiMessages } from '../uiMessages';

const RECORD_NAME_FIELDS = ['originalFileName', 'OriginalFileName', 'fileName', 'FileName', 'storedFileName', 'StoredFileName'];
const DESCRIPTION_SECURITY_MESSAGE = 'HTML/script tags are not allowed in description.';
const DESCRIPTION_ERROR_FALLBACK = 'Unable to update description right now.';
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
const ANGLE_BRACKET_PATTERN = /[<>]/g;

function sanitizeDescriptionText(value) {
  return String(value ?? '')
    .replace(CONTROL_CHARACTER_PATTERN, '')
    .replace(ANGLE_BRACKET_PATTERN, '');
}

function normalizeDescriptionForSave(value) {
  return sanitizeDescriptionText(value).trim();
}

function EditableDescriptionField({
  value,
  canEdit,
  isTranscript,
  record,
  item,
  onSaveDescription,
  canDelete,
  onDeleteAction,
  deleteButtonClassName,
  deleteButtonStyle,
  deleteAriaLabel
}) {
  const safeValue = useMemo(() => sanitizeDescriptionText(value), [value]);
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(safeValue);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isEditing) {
      return;
    }

    setDraftValue(safeValue);
    setErrorMessage('');
  }, [isEditing, safeValue]);

  const normalizedOriginalValue = useMemo(() => normalizeDescriptionForSave(safeValue), [safeValue]);
  const normalizedDraftValue = useMemo(() => normalizeDescriptionForSave(draftValue), [draftValue]);
  const isDirty = normalizedDraftValue !== normalizedOriginalValue;

  function handleStartEdit() {
    if (!canEdit || isSaving) {
      return;
    }

    setDraftValue(safeValue);
    setErrorMessage('');
    setIsEditing(true);
  }

  function handleCancelEdit() {
    if (isSaving) {
      return;
    }

    setDraftValue(safeValue);
    setErrorMessage('');
    setIsEditing(false);
  }

  function handleChange(event) {
    const rawValue = event.target.value ?? '';
    const sanitizedValue = sanitizeDescriptionText(rawValue);

    setDraftValue(sanitizedValue);
    setErrorMessage(rawValue !== sanitizedValue ? DESCRIPTION_SECURITY_MESSAGE : '');
  }

  async function handleSave() {
    if (!canEdit || !isDirty || isSaving || typeof onSaveDescription !== 'function') {
      return;
    }

    setIsSaving(true);
    setErrorMessage('');

    try {
      const result = await onSaveDescription({
        item,
        record,
        description: normalizedDraftValue,
        isTranscript
      });

      const savedDescription = normalizeDescriptionForSave(result?.description ?? normalizedDraftValue);
      setDraftValue(savedDescription);
      setIsEditing(false);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error?.message || DESCRIPTION_ERROR_FALLBACK);
    } finally {
      setIsSaving(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEdit();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    }
  }

  return (
    <>
      <div className="case-file-description-col">
        <input
          type="text"
          className="description-input case-file-description-input"
          placeholder="Description (optional)"
          value={isEditing ? draftValue : safeValue}
          readOnly={!isEditing}
          disabled={isSaving}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck="false"
        />
        {errorMessage ? (
          <div className="case-file-description-error" role="alert">{errorMessage}</div>
        ) : null}
      </div>

      {(canEdit || canDelete) ? (
        <div className="case-file-row-actions">
          {isEditing ? (
            <>
              <button
                type="button"
                className="icon-action-button case-file-action-button case-file-action-button-save"
                onClick={handleSave}
                disabled={!isDirty || isSaving}
                aria-label={`Save ${isTranscript ? 'transcript' : 'media'} description`}
                title="Save"
              >
                {isSaving ? <SpinnerIcon title="Saving" /> : <CheckIcon />}
              </button>
              <button
                type="button"
                className="icon-action-button case-file-action-button case-file-action-button-cancel"
                onClick={handleCancelEdit}
                disabled={isSaving}
                aria-label={`Cancel editing ${isTranscript ? 'transcript' : 'media'} description`}
                title="Cancel"
              >
                <XIcon />
              </button>
            </>
          ) : (
            <>
              {canEdit ? (
                <button
                  type="button"
                  className="icon-action-button case-file-action-button"
                  onClick={handleStartEdit}
                  aria-label={`Edit ${isTranscript ? 'transcript' : 'media'} description`}
                  title="Edit description"
                >
                  <EditIcon />
                </button>
              ) : null}
              {canDelete && typeof onDeleteAction === 'function' ? (
                <button
                  className={deleteButtonClassName}
                  type="button"
                  style={deleteButtonStyle}
                  onClick={() => onDeleteAction(item)}
                  aria-label={deleteAriaLabel}
                  title="Delete"
                >
                  <TrashIcon />
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </>
  );
}

EditableDescriptionField.propTypes = {
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  isTranscript: PropTypes.bool,
  record: PropTypes.object,
  item: PropTypes.object,
  onSaveDescription: PropTypes.func,
  canDelete: PropTypes.bool,
  onDeleteAction: PropTypes.func,
  deleteButtonClassName: PropTypes.string,
  deleteButtonStyle: PropTypes.shape({
    color: PropTypes.string
  }),
  deleteAriaLabel: PropTypes.string
};

EditableDescriptionField.defaultProps = {
  value: '',
  canEdit: false,
  isTranscript: false,
  record: null,
  item: null,
  onSaveDescription: null,
  canDelete: false,
  onDeleteAction: null,
  deleteButtonClassName: 'icon-action-button',
  deleteButtonStyle: undefined,
  deleteAriaLabel: 'Delete file'
};

function CaseFileTranscriptRow({
  transcriptName,
  transcriptTone,
  transcriptRecord,
  getFirstRecordValue,
  canEditDescription,
  canDelete,
  transcriptDeleteButtonClassName,
  transcriptDeleteButtonStyle,
  item,
  onDeleteTranscript,
  onSaveDescription
}) {
  const transcriptDescription = getFirstRecordValue(transcriptRecord, ['description', 'Description']) || '';

  return (
    <div className="case-file-transcript-row case-file-transcript-row-with-desc">
      <div className="case-file-transcript-heading-compact">
        <div className="case-file-transcript-content">
          <div className="case-file-transcript-title">{transcriptName}</div>
          <ScanStatusBadge tone={transcriptTone} />
        </div>
      </div>

      <EditableDescriptionField
        value={transcriptDescription}
        canEdit={canEditDescription}
        isTranscript
        record={transcriptRecord}
        item={item}
        onSaveDescription={onSaveDescription}
        canDelete={canDelete}
        onDeleteAction={onDeleteTranscript}
        deleteButtonClassName={transcriptDeleteButtonClassName}
        deleteButtonStyle={transcriptDeleteButtonStyle}
        deleteAriaLabel={`Delete transcript file ${transcriptName}`}
      />
    </div>
  );
}

CaseFileTranscriptRow.propTypes = {
  transcriptName: PropTypes.string,
  transcriptTone: PropTypes.string,
  transcriptRecord: PropTypes.object,
  getFirstRecordValue: PropTypes.func.isRequired,
  canEditDescription: PropTypes.bool,
  canDelete: PropTypes.bool,
  transcriptDeleteButtonClassName: PropTypes.string,
  transcriptDeleteButtonStyle: PropTypes.shape({
    color: PropTypes.string
  }),
  item: PropTypes.object,
  onDeleteTranscript: PropTypes.func,
  onSaveDescription: PropTypes.func
};

CaseFileTranscriptRow.defaultProps = {
  transcriptName: 'Transcript',
  transcriptTone: 'none',
  transcriptRecord: null,
  canEditDescription: false,
  canDelete: false,
  transcriptDeleteButtonClassName: 'icon-action-button',
  transcriptDeleteButtonStyle: { color: '#64748b' },
  item: null,
  onDeleteTranscript: null,
  onSaveDescription: null
};

function CaseFileTranscriptEmptyState({
  canAddTranscript,
  isAddingTranscript,
  onBrowseAddTranscript,
  item,
  setRemoteTranscriptInputRef,
  mediaId,
  transcriptAccept,
  onRemoteTranscriptFileChange
}) {
  return (
    <div className="case-file-transcript-empty text-muted">
      <span>{UiMessages.transcriptNone}</span>
      {canAddTranscript ? (
        <>
          {' - '}
          <button
            type="button"
            className="case-file-add-transcript-link"
            onClick={() => onBrowseAddTranscript?.(item)}
            disabled={Boolean(isAddingTranscript)}
          >
            {isAddingTranscript ? `${UiMessages.transcriptAddAction}...` : UiMessages.transcriptAddAction}
          </button>
          <input
            ref={(element) => setRemoteTranscriptInputRef?.(mediaId, element)}
            type="file"
            className="d-none"
            accept={transcriptAccept || '.pdf,application/pdf'}
            onChange={(event) => onRemoteTranscriptFileChange?.(item, event)}
          />
        </>
      ) : null}
    </div>
  );
}

CaseFileTranscriptEmptyState.propTypes = {
  canAddTranscript: PropTypes.bool,
  isAddingTranscript: PropTypes.bool,
  onBrowseAddTranscript: PropTypes.func,
  item: PropTypes.object,
  setRemoteTranscriptInputRef: PropTypes.func,
  mediaId: PropTypes.string,
  transcriptAccept: PropTypes.string,
  onRemoteTranscriptFileChange: PropTypes.func
};

CaseFileTranscriptEmptyState.defaultProps = {
  canAddTranscript: false,
  isAddingTranscript: false,
  onBrowseAddTranscript: null,
  item: null,
  setRemoteTranscriptInputRef: null,
  mediaId: '',
  transcriptAccept: '.pdf,application/pdf',
  onRemoteTranscriptFileChange: null
};

export function CaseFileCard({
  item,
  isReadOnly,
  canOpenPlayer,
  onDelete,
  onDeleteTranscript,
  onOpenPlayer,
  onBrowseAddTranscript,
  onRemoteTranscriptFileChange,
  setRemoteTranscriptInputRef,
  transcriptAccept,
  isAddingTranscript,
  getEffectiveTranscriptRecord,
  resolveCaseFileMediaTone,
  resolveCaseFileTranscriptTone,
  isVirusDetectedMediaRecord,
  isTranscriptVirusDetected,
  isVirusStatus,
  isLocalUploadFailed,
  getFirstRecordValue,
  getRecordMediaId,
  getRecordTranscriptFileId,
  onSaveDescription
}) {
  const mediaRecord = item?.mediaRecord ?? {};
  const transcriptRecord = getEffectiveTranscriptRecord(
    mediaRecord,
    item?.transcriptRecord ?? null,
  );
  const localUploadItem = item?.localUploadItem ?? null;
  const mediaTone = resolveCaseFileMediaTone(mediaRecord, localUploadItem);
  const transcriptTone = resolveCaseFileTranscriptTone(
    transcriptRecord,
    localUploadItem,
    mediaRecord,
  );
  const canDelete = !isReadOnly;
  const canEditDescription = !isReadOnly && typeof onSaveDescription === 'function';
  const hasRejectedAppearance = mediaTone === 'quarantine'
    || transcriptTone === 'quarantine'
    || mediaTone === 'failed'
    || transcriptTone === 'failed'
    || isLocalUploadFailed(localUploadItem);
  const mediaDeleteButtonStyle = mediaTone === 'quarantine' || mediaTone === 'failed'
    ? { color: '#b91c1c' }
    : { color: '#64748b' };
  const transcriptDeleteButtonStyle = transcriptTone === 'quarantine' || transcriptTone === 'failed'
    ? { color: '#b91c1c' }
    : { color: '#64748b' };
  const mediaDeleteButtonClassName = mediaTone === 'quarantine' || mediaTone === 'failed'
    ? 'icon-action-button file-delete-button case-file-delete-button'
    : 'icon-action-button';
  const transcriptDeleteButtonClassName = transcriptTone === 'quarantine' || transcriptTone === 'failed'
    ? 'icon-action-button file-delete-button case-file-transcript-delete-button'
    : 'icon-action-button';
  const mediaName = getFirstRecordValue(mediaRecord, RECORD_NAME_FIELDS) || getRecordMediaId(mediaRecord) || 'Media file';
  const transcriptName = getFirstRecordValue(transcriptRecord, RECORD_NAME_FIELDS) || getRecordTranscriptFileId(transcriptRecord) || 'Transcript';
  const canOpenMediaPlayer = canOpenPlayer && typeof onOpenPlayer === 'function';
  const currentDescription = getFirstRecordValue(mediaRecord, ['description', 'Description']) || '';
  const mediaId = getRecordMediaId(mediaRecord) || '';
  const canAddTranscript = !isReadOnly && !transcriptRecord && Boolean(mediaId) && typeof onBrowseAddTranscript === 'function';

  return (
    <div
      className={`case-file-card ${canDelete ? 'case-file-card-editable' : 'case-file-card-locked'} ${hasRejectedAppearance ? 'case-file-card-virus' : ''}`}
    >
      <div className="case-file-row-header case-file-row-header-with-desc">
        <div className="file-heading-row case-file-heading-row case-file-heading-compact">
          <div className="file-heading-content case-file-heading-content">
            {canOpenMediaPlayer ? (
              <button
                type="button"
                className="case-file-title case-file-open-player-button"
                onClick={() => onOpenPlayer(item)}
                aria-label={`Open media player for ${mediaName}`}
              >
                {mediaName}
              </button>
            ) : (
              <div
                className="case-file-title"
                style={{ textDecoration: 'none', cursor: 'default' }}
              >
                {mediaName}
              </div>
            )}
            <ScanStatusBadge tone={mediaTone} />
          </div>
        </div>

        <EditableDescriptionField
          value={currentDescription}
          canEdit={canEditDescription}
          record={mediaRecord}
          item={item}
          onSaveDescription={onSaveDescription}
          canDelete={canDelete}
          onDeleteAction={onDelete}
          deleteButtonClassName={mediaDeleteButtonClassName}
          deleteButtonStyle={mediaDeleteButtonStyle}
          deleteAriaLabel={`Delete media file ${mediaName}`}
        />
      </div>

      {transcriptRecord ? (
        <CaseFileTranscriptRow
          transcriptName={transcriptName}
          transcriptTone={transcriptTone}
          transcriptRecord={transcriptRecord}
          getFirstRecordValue={getFirstRecordValue}
          canEditDescription={canEditDescription}
          canDelete={canDelete}
          transcriptDeleteButtonClassName={transcriptDeleteButtonClassName}
          transcriptDeleteButtonStyle={transcriptDeleteButtonStyle}
          item={item}
          onDeleteTranscript={onDeleteTranscript}
          onSaveDescription={onSaveDescription}
        />
      ) : (
        <CaseFileTranscriptEmptyState
          canAddTranscript={canAddTranscript}
          isAddingTranscript={isAddingTranscript}
          onBrowseAddTranscript={onBrowseAddTranscript}
          item={item}
          setRemoteTranscriptInputRef={setRemoteTranscriptInputRef}
          mediaId={mediaId}
          transcriptAccept={transcriptAccept}
          onRemoteTranscriptFileChange={onRemoteTranscriptFileChange}
        />
      )}
    </div>
  );
}

CaseFileCard.propTypes = {
  item: PropTypes.shape({
    mediaRecord: PropTypes.object,
    transcriptRecord: PropTypes.object,
    localUploadItem: PropTypes.shape({
      scanStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
      transcriptScanStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
    })
  }),
  isReadOnly: PropTypes.bool,
  canOpenPlayer: PropTypes.bool,
  onDelete: PropTypes.func,
  onDeleteTranscript: PropTypes.func,
  onOpenPlayer: PropTypes.func,
  onBrowseAddTranscript: PropTypes.func,
  onRemoteTranscriptFileChange: PropTypes.func,
  setRemoteTranscriptInputRef: PropTypes.func,
  transcriptAccept: PropTypes.string,
  isAddingTranscript: PropTypes.bool,
  getEffectiveTranscriptRecord: PropTypes.func.isRequired,
  resolveCaseFileMediaTone: PropTypes.func.isRequired,
  resolveCaseFileTranscriptTone: PropTypes.func.isRequired,
  isVirusDetectedMediaRecord: PropTypes.func.isRequired,
  isTranscriptVirusDetected: PropTypes.func.isRequired,
  isVirusStatus: PropTypes.func.isRequired,
  isLocalUploadFailed: PropTypes.func.isRequired,
  getFirstRecordValue: PropTypes.func.isRequired,
  getRecordMediaId: PropTypes.func.isRequired,
  getRecordTranscriptFileId: PropTypes.func.isRequired,
  onSaveDescription: PropTypes.func
};

CaseFileCard.defaultProps = {
  item: null,
  isReadOnly: false,
  canOpenPlayer: false,
  onDelete: null,
  onDeleteTranscript: null,
  onOpenPlayer: null,
  onBrowseAddTranscript: null,
  onRemoteTranscriptFileChange: null,
  setRemoteTranscriptInputRef: null,
  transcriptAccept: '.pdf,application/pdf',
  isAddingTranscript: false,
  onSaveDescription: null
};
